/** 画面の割り振り（どの住所でどの画面を出すか）。 */
import { IntakesPage } from './pages/IntakesPage.js';
import type { ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { RequireSession } from './components/RequireSession.js';
import { AiKnowledgeDetailPage } from './pages/AiKnowledgeDetailPage.js';
import { AiKnowledgePage } from './pages/AiKnowledgePage.js';
import { AiPendingPage } from './pages/AiPendingPage.js';
import { AiQuestionDetailPage } from './pages/AiQuestionDetailPage.js';
import { AiRestrictedTopicsPage } from './pages/AiRestrictedTopicsPage.js';
import { AiUnansweredPage } from './pages/AiUnansweredPage.js';
import { AiUsagePage } from './pages/AiUsagePage.js';
import { AnnouncementsPage } from './pages/AnnouncementsPage.js';
import { AuditLogsPage } from './pages/AuditLogsPage.js';
import { DataExportsPage } from './pages/DataExportsPage.js';
import { ChannelCreatePage } from './pages/ChannelCreatePage.js';
import { ChannelDetailPage } from './pages/ChannelDetailPage.js';
import { ChannelsPage } from './pages/ChannelsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { DirectEvidencePage } from './pages/DirectEvidencePage.js';
import { DormantMembersPage } from './pages/DormantMembersPage.js';
import { InactiveSupportPage } from './pages/InactiveSupportPage.js';
import { DormantRoomsPage } from './pages/DormantRoomsPage.js';
import { JoinRequestsPage } from './pages/JoinRequestsPage.js';
import { LicensesPage } from './pages/LicensesPage.js';
import { LoginLimitsPage } from './pages/LoginLimitsPage.js';
import { LoginPage } from './pages/LoginPage.js';
import { MemberDetailPage } from './pages/MemberDetailPage.js';
import { MembersPage } from './pages/MembersPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { RoleEditPage } from './pages/RoleEditPage.js';
import { RolesPage } from './pages/RolesPage.js';
import { RosterImportDetailPage } from './pages/RosterImportDetailPage.js';
import { RosterImportPage } from './pages/RosterImportPage.js';
import { RosterImportsPage } from './pages/RosterImportsPage.js';
import { SettingsGroupPage } from './pages/SettingsGroupPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SitesPage } from './pages/SitesPage.js';
import { StickersPage } from './pages/StickersPage.js';
import { UsagePage } from './pages/UsagePage.js';
import { VerificationSmsPage } from './pages/VerificationSmsPage.js';
import { BrandTheme } from './theme/BrandTheme.js';

export function App(): ReactNode {
  return (
    <>
      {/* 会社ごとの色を画面に差し込む（見た目だけ。画面は描かない） */}
      <BrandTheme />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* 使っている部品のライセンス（issue #486）。ログインしていなくても開ける */}
        <Route path="/licenses" element={<LicensesPage />} />
        {/* ここから内側は、ログインしていないとログイン画面へ戻る */}
        <Route element={<RequireSession />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/audit-logs" element={<AuditLogsPage />} />
          {/* 月ごとの利用状況（issue #115） */}
          <Route path="/usage" element={<UsagePage />} />
          {/* 休眠の棚卸し（issue #96）。人とトークで画面を分ける（できることが違うため） */}
          <Route path="/dormancy/members" element={<DormantMembersPage />} />
          <Route path="/support/inactive" element={<InactiveSupportPage />} />
          <Route path="/dormancy/rooms" element={<DormantRoomsPage />} />
          <Route path="/data-export" element={<DataExportsPage />} />
          {/* 検証のサーバだけの「認証番号の控え」（issue #342） */}
          <Route path="/verification-sms" element={<VerificationSmsPage />} />
          {/* 部屋は「一覧」「作る」「1つを開く」の3画面に分ける（docs/06 原則3） */}
          <Route path="/rooms" element={<ChannelsPage />} />
          <Route path="/rooms/new" element={<ChannelCreatePage />} />
          {/* 1対1は一覧に出さないので、「事故対応」の指定はここから付ける（決定27・issue #242） */}
          <Route path="/rooms/direct" element={<DirectEvidencePage />} />
          <Route path="/rooms/:id" element={<ChannelDetailPage />} />
          <Route path="/intakes" element={<IntakesPage />} />
          <Route path="/notices" element={<AnnouncementsPage />} />
          {/* 名簿に無い方からの参加の申し込み（決定34） */}
          <Route path="/join-requests" element={<JoinRequestsPage />} />
          <Route path="/members" element={<MembersPage />} />
          <Route path="/members/:id" element={<MemberDetailPage />} />
          {/* 取り込みは「これから行う画面」と「あとから見返す画面」を分けてある（docs/06 原則3） */}
          <Route path="/roster/import" element={<RosterImportPage />} />
          <Route path="/roster/imports" element={<RosterImportsPage />} />
          <Route path="/roster/imports/:id" element={<RosterImportDetailPage />} />
          <Route path="/roles" element={<RolesPage />} />
          {/* 役職の呼び名・できることを変える（issue #124）。一覧（見る）と変える画面を分ける */}
          <Route path="/roles/:key" element={<RoleEditPage />} />
          {/* 会社の設定（issue #124）。一覧（見る）と、まとまりごとに変える画面を分ける */}
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/settings/:group" element={<SettingsGroupPage />} />
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/login-limits" element={<LoginLimitsPage />} />
          {/* スタンプの取り込み・並び順・取り下げ（issue #260） */}
          <Route path="/stamps" element={<StickersPage />} />
          {/* AI先輩（issue #109・#185）。承認待ち → 1件 → 承認、の順に進む動線 */}
          <Route path="/ai/pending" element={<AiPendingPage />} />
          <Route path="/ai/knowledge" element={<AiKnowledgePage />} />
          <Route path="/ai/knowledge/:id" element={<AiKnowledgeDetailPage />} />
          <Route path="/ai/questions/:id" element={<AiQuestionDetailPage />} />
          <Route path="/ai/unanswered" element={<AiUnansweredPage />} />
          <Route path="/ai/restricted-topics" element={<AiRestrictedTopicsPage />} />
          <Route path="/ai/usage" element={<AiUsagePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
